import { DateTime } from "luxon";
import type { BackgroundJob, BackgroundJobKind } from "@/api/background-job/_models/BackgroundJob";
import type { BackgroundJobScheduleInput } from "@/api/background-job/_models/BackgroundJobSchedule";

/** A job kind's label ("Build snapshot"), or the kind itself when unknown. */
export const toJobKindLabel = (kinds: BackgroundJobKind[], kind: string): string => {
  return kinds.find((entry) => entry.kind === kind)?.label ?? kind;
};

const toCount = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (Array.isArray(value)) return value.length;
  return 0;
};

/** One line on what a finished job did, read from its kind's result shape; a failed job's error. */
export const toJobSummary = (job: BackgroundJob): string => {
  if (job.status === "FAILED") return job.error ?? "Failed";
  if (job.status !== "DONE") return "";
  const result = (job.result ?? {}) as Record<string, unknown>;
  if (job.kind === "buildSnapshot") {
    if (result.skipped === true) return "Already built — left as it was";
    return `${toCount(result.totalRows).toLocaleString()} rows`;
  }
  if (job.kind === "pullEstoreInventory") {
    const pulled = toCount(result.pulled);
    const failed = toCount(result.failed);
    let text = `${pulled} new file(s) pulled`;
    if (pulled === 0) text = "Nothing new on the SFTP";
    if (failed > 0) text = `${text}; ${failed} failed`;
    return text;
  }
  if (job.kind === "buildOrderLineReport") {
    return `${toCount(result.rows).toLocaleString()} lines, ${toCount(result.orders).toLocaleString()} orders`;
  }
  if (job.kind === "importOrderStatuses") {
    const toe = result.toe as Record<string, unknown> | undefined;
    const threePl = result.threePl as Record<string, Record<string, unknown> | undefined> | undefined;
    const written = toCount(toe?.written) + toCount(threePl?.toll?.written) + toCount(threePl?.estore?.written);
    return `${written.toLocaleString()} statuses written`;
  }
  return "Done";
};

/** The names of the files a finished job wrote (its result's `files`), e.g. order-report-261027.xlsx. */
export const toJobFileNameList = (job: BackgroundJob): string[] => {
  if (job.status !== "DONE") return [];
  const files = (job.result as { files?: unknown } | null)?.files;
  if (!Array.isArray(files)) return [];
  return files.flatMap((file) => {
    const name = (file as { name?: unknown } | null)?.name;
    return typeof name === "string" ? [name] : [];
  });
};

/** How long a job ran ("42s", "3m 05s"); empty until it has started. */
export const toJobDuration = (job: BackgroundJob, now: number): string => {
  if (!job.startedAt) return "";
  const start = DateTime.fromISO(job.startedAt).toMillis();
  let end = now;
  if (job.finishedAt) end = DateTime.fromISO(job.finishedAt).toMillis();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return `${minutes}m ${rest}s`;
};

const WEEKDAY_SHORT = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** When a schedule runs, in words (Sydney time): "Every 15 min", "Hourly at :05", "Weekdays at 06:00", "Mon, Wed at 18:30". */
export const toJobScheduleDescription = (schedule: BackgroundJobScheduleInput): string => {
  if (schedule.frequency === "MINUTES") return `Every ${schedule.intervalMinutes ?? "?"} min`;
  if (schedule.frequency === "HOURLY") return `Hourly at :${String(schedule.minute ?? 0).padStart(2, "0")}`;
  const time = schedule.time ?? "??:??";
  const days = [...schedule.weekdays].sort((a, b) => a - b);
  if (days.length === 0 || days.length === 7) return `Daily at ${time}`;
  if (days.join(",") === "1,2,3,4,5") return `Weekdays at ${time}`;
  if (days.join(",") === "6,7") return `Weekends at ${time}`;
  return `${days.map((day) => WEEKDAY_SHORT[day]).join(", ")} at ${time}`;
};
